<template>
  <!--
    CustomerNotesSection
    ====================

    Read-only list of customer notes + a button that opens a SEPARATE
    editor dialog (CustomerNoteEditorDialog) for create/edit.

    Two operating modes, decided automatically by `customerId`:

      1. SAVED   — `customerId` is a real id  → list is loaded from
                   the API; create/edit/delete go straight through API.
                   Used by CustomerEditDialog.

      2. DRAFT   — `customerId` is null/0     → list is held locally;
                   the parent (NewCustomerDialog) reads drafts via the
                   exposed `getDraftNotes()` after customer create and
                   POSTs them itself. No API calls happen here in this
                   mode.

    What we deliberately DO NOT do here (and why):

      * No `v-model` on a parent reactive object — that triggered a
        reactive feedback loop in the previous version and locked the
        whole NewCustomerDialog. Drafts live ONLY inside this component;
        the parent reads them via a defineExpose'd function.

      * No inline form. Add/Edit always opens the editor as a SECOND
        dialog above the parent dialog. If the editor crashes, only
        the editor falls — the parent dialog stays usable.

      * No `auto-grow` + `hide-details` on a textarea (Vuetify 3 zero-
        height bug). The editor uses fixed `rows` instead.

      * No nested outlined v-card inside another outlined v-card —
        we render the list with simple bordered divs only.
  -->
  <v-card variant="outlined" class="mb-3">
    <!-- ============ HEADER ============ -->
    <div
      class="d-flex align-center pa-3 cursor-pointer"
      @click="expanded = !expanded"
    >
      <v-icon size="20" class="mr-2" color="primary">mdi-note-text-outline</v-icon>
      <div class="text-subtitle-2 font-weight-bold">
        Notes ({{ totalCount }})
      </div>

      <v-spacer />

      <v-btn
        size="small"
        variant="tonal"
        color="accent"
        @click.stop="openCreate"
      >
        <v-icon start size="16">mdi-plus</v-icon>
        Add Note
      </v-btn>

      <v-icon size="20" class="ml-2">
        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
      </v-icon>
    </div>

    <!-- ============ BODY ============ -->
    <v-expand-transition>
      <div v-if="expanded">
        <v-divider />
        <div class="pa-3">
          <!-- Loading -->
          <div v-if="loading" class="text-center py-3">
            <v-progress-circular indeterminate size="24" />
          </div>

          <!-- Empty -->
          <div
            v-else-if="!totalCount"
            class="text-center text-body-2 text-medium-emphasis py-3"
          >
            No notes yet. Click <strong>Add Note</strong> to add the first one.
          </div>

          <!-- Notes list -->
          <template v-else>
            <!-- Saved notes (from API) -->
            <v-card
              v-for="note in notes"
              :key="`s-${note.id}`"
              variant="outlined"
              class="mb-2"
              :class="{ 'note-system': note.is_system }"
            >
              <div class="pa-3">
                <div class="d-flex align-center ga-2 mb-2 flex-wrap">
                  <v-icon
                    size="18"
                    :color="note.is_system ? 'medium-emphasis' : 'primary'"
                  >
                    {{ note.is_system ? 'mdi-cog-outline' : 'mdi-account-circle-outline' }}
                  </v-icon>
                  <span
                    class="text-body-2 font-weight-medium"
                    :class="{ 'text-medium-emphasis font-italic': note.is_system }"
                  >
                    {{ note.is_system ? 'System' : (note.created_by_name || 'Unknown') }}
                  </span>
                  <v-chip
                    v-if="note.is_system"
                    size="x-small"
                    color="medium-emphasis"
                    variant="tonal"
                  >
                    legacy
                  </v-chip>
                  <span
                    v-else-if="note.created_by_role"
                    class="text-caption text-medium-emphasis"
                  >
                    · {{ note.created_by_role }}
                  </span>
                  <span class="text-caption text-medium-emphasis">
                    · {{ formatDateTime(note.created_at) }}
                  </span>
                  <v-chip
                    v-if="note.is_edited"
                    size="x-small"
                    variant="tonal"
                    color="medium-emphasis"
                  >
                    edited
                  </v-chip>

                  <v-spacer />

                  <template v-if="note.can_edit">
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      @click="openEdit(note)"
                    >
                      <v-icon size="16">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      color="error"
                      @click="confirmDelete(note)"
                    >
                      <v-icon size="16">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                </div>

                <div
                  class="text-body-2"
                  style="white-space: pre-wrap;"
                >{{ note.content }}</div>

                <div
                  v-if="note.attachments && note.attachments.length"
                  class="d-flex flex-wrap ga-2 mt-2"
                >
                  <v-chip
                    v-for="att in note.attachments"
                    :key="att.id"
                    :href="att.download_url"
                    target="_blank"
                    size="small"
                    variant="tonal"
                    color="primary"
                  >
                    <v-icon start size="14">mdi-paperclip</v-icon>
                    {{ att.file_name }} · {{ formatBytes(att.file_size) }}
                  </v-chip>
                </div>
              </div>
            </v-card>

            <!--
              Draft notes — only present in draft mode (NewCustomerDialog).
              They live entirely on the client; the parent posts them after
              the customer is created.
            -->
            <v-card
              v-for="(draft, idx) in draftNotes"
              :key="`d-${idx}`"
              variant="outlined"
              class="mb-2 note-draft"
            >
              <div class="pa-3">
                <div class="d-flex align-center ga-2 mb-2 flex-wrap">
                  <v-icon size="18" color="accent">mdi-pencil-outline</v-icon>
                  <span class="text-body-2 font-weight-medium">Draft</span>
                  <v-chip size="x-small" color="accent" variant="tonal">
                    will be saved with the customer
                  </v-chip>
                  <v-spacer />
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    @click="openEditDraft(idx)"
                  >
                    <v-icon size="16">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="error"
                    @click="removeDraft(idx)"
                  >
                    <v-icon size="16">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
                <div
                  class="text-body-2"
                  style="white-space: pre-wrap;"
                >{{ draft.content }}</div>
                <div
                  v-if="draft.files.length"
                  class="d-flex flex-wrap ga-2 mt-2"
                >
                  <v-chip
                    v-for="(f, i) in draft.files"
                    :key="i"
                    size="small"
                    variant="tonal"
                    color="accent"
                  >
                    <v-icon start size="14">mdi-paperclip</v-icon>
                    {{ f.name }} · {{ formatBytes(f.size) }}
                  </v-chip>
                </div>
              </div>
            </v-card>
          </template>
        </div>
      </div>
    </v-expand-transition>

    <!--
      Editor dialog — separate dialog, renders on top of the parent dialog.
      Fully isolated from this component's reactivity: it receives a frozen
      payload and emits the result back. We keep ALL reactive state for the
      input here in the parent (this component) and pass primitives in/out.
    -->
    <CustomerNoteEditorDialog
      v-model="editorOpen"
      :initial-note="editingPayload"
      @saved="onEditorSaved"
    />
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { customerNotesApi } from '@/modules/jobs/services/customerNotesApi'
import { useNotify } from '@/composables/useNotify'
import CustomerNoteEditorDialog from '@/components/CustomerNoteEditorDialog.vue'

const props = defineProps({
  /**
   * Customer id from the server. Pass null/0 in DRAFT mode (the
   * NewCustomerDialog pre-create flow); pass a real id in SAVED mode
   * (CustomerEditDialog).
   */
  customerId: { type: [Number, String, null], default: null },
})

/**
 * `count-changed` lets a parent (CustomerCardDialog, CustomerEditDialog)
 * show a counter chip in its header without owning the list itself.
 * Emits the *displayed* count = saved notes + drafts.
 */
const emit = defineEmits(['count-changed'])

const { notify, confirm } = useNotify()

const expanded = ref(true)
const loading = ref(false)

// Saved notes loaded from the API (only meaningful in SAVED mode).
const notes = ref([])

// Drafts staged locally (only meaningful in DRAFT mode).
// Each: { content: string, files: File[] }
const draftNotes = ref([])

const editorOpen = ref(false)

/**
 * Frozen payload describing what the editor should show. We rebuild it
 * from scratch on every open — no reactive sharing with the editor.
 *
 *   { type: 'create' | 'saved' | 'draft',
 *     noteId?: number,            // saved
 *     draftIdx?: number,          // draft
 *     content: string,
 *     files: File[],
 *     existingAttachments: AttachmentRow[] }
 */
const editingPayload = ref(null)

const draftMode = computed(() => !props.customerId)
const totalCount = computed(() => notes.value.length + draftNotes.value.length)

// Re-emit whenever the count changes so parent header chips stay in sync.
watch(totalCount, (n) => emit('count-changed', n), { immediate: true })

// =====================================================================
// LOAD (saved mode)
// =====================================================================
async function loadNotes() {
  if (!props.customerId) {
    notes.value = []
    return
  }
  loading.value = true
  try {
    notes.value = await customerNotesApi.list(props.customerId)
  } catch (err) {
    console.error('[CustomerNotesSection] load failed', err)
    notify.error('Failed to load notes')
  } finally {
    loading.value = false
  }
}
watch(() => props.customerId, loadNotes)
onMounted(loadNotes)

// =====================================================================
// OPEN EDITOR
// =====================================================================
function openCreate() {
  editingPayload.value = {
    type: 'create',
    content: '',
    files: [],
    existingAttachments: [],
  }
  editorOpen.value = true
}

function openEdit(note) {
  editingPayload.value = {
    type: 'saved',
    noteId: note.id,
    content: note.content,
    files: [],
    existingAttachments: Array.isArray(note.attachments) ? [...note.attachments] : [],
  }
  editorOpen.value = true
}

function openEditDraft(idx) {
  const draft = draftNotes.value[idx]
  if (!draft) return
  editingPayload.value = {
    type: 'draft',
    draftIdx: idx,
    content: draft.content,
    files: [...draft.files],
    existingAttachments: [],
  }
  editorOpen.value = true
}

// =====================================================================
// HANDLE EDITOR RESULT
// =====================================================================
async function onEditorSaved(result) {
  // result: { content: string, files: File[], existingAttachmentIdsToRemove: number[] }
  const editing = editingPayload.value
  if (!editing) return

  // ---- CREATE NEW ----
  if (editing.type === 'create') {
    if (draftMode.value) {
      draftNotes.value.push({
        content: result.content,
        files: [...result.files],
      })
      return
    }
    try {
      await customerNotesApi.create(props.customerId, {
        content: result.content,
        files: result.files,
      })
      await loadNotes()
      notify.success('Note saved')
    } catch (err) {
      console.error('[CustomerNotesSection] create failed', err)
      notify.error('Failed to save note')
    }
    return
  }

  // ---- EDIT DRAFT ----
  if (editing.type === 'draft') {
    if (typeof editing.draftIdx === 'number' && draftNotes.value[editing.draftIdx]) {
      draftNotes.value[editing.draftIdx] = {
        content: result.content,
        files: [...result.files],
      }
    }
    return
  }

  // ---- EDIT SAVED ----
  if (editing.type === 'saved') {
    try {
      await customerNotesApi.update(props.customerId, editing.noteId, {
        content: result.content,
      })
      const toRemove = result.existingAttachmentIdsToRemove || []
      for (const attId of toRemove) {
        try {
          await customerNotesApi.removeAttachment(props.customerId, editing.noteId, attId)
        } catch (e) {
          console.error('[CustomerNotesSection] attachment delete failed', e)
        }
      }
      if (result.files.length) {
        await customerNotesApi.uploadAttachments(props.customerId, editing.noteId, result.files)
      }
      await loadNotes()
      notify.success('Note saved')
    } catch (err) {
      console.error('[CustomerNotesSection] update failed', err)
      notify.error('Failed to save note')
    }
  }
}

// =====================================================================
// DELETE
// =====================================================================
async function confirmDelete(note) {
  const ok = await confirm({
    title: 'Delete this note?',
    message: 'This will permanently remove the note and its attachments.',
    confirmText: 'Delete',
    type: 'warning',
  })
  if (!ok) return
  try {
    await customerNotesApi.remove(props.customerId, note.id)
    await loadNotes()
    notify.success('Note deleted')
  } catch (err) {
    console.error('[CustomerNotesSection] delete failed', err)
    notify.error('Failed to delete')
  }
}

function removeDraft(idx) {
  draftNotes.value.splice(idx, 1)
}

// =====================================================================
// FORMAT HELPERS
// =====================================================================
function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDateTime(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch (_) {
    return iso
  }
}

// =====================================================================
// EXPOSE — used by NewCustomerDialog after a successful customer create.
// We never write into draftNotes from the parent; the parent only READS.
// =====================================================================
defineExpose({
  /** Snapshot copy of current drafts. Safe to mutate by the caller. */
  getDraftNotes() {
    return draftNotes.value.map((d) => ({
      content: d.content,
      files: [...d.files],
    }))
  },
  /** Discard all drafts (parent calls after dialog opens fresh). */
  resetDrafts() {
    draftNotes.value = []
  },
})
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.note-system {
  background-color: rgba(var(--v-theme-on-surface), 0.015);
}
.note-draft {
  border-color: rgb(var(--v-theme-accent)) !important;
  background-color: rgba(var(--v-theme-accent), 0.05);
}
</style>
